/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Center, Flex, VStack } from 'chakra-v3';
import MinimalUserHeader from '../components/headers/MinimalUserHeader';
import CardShell from '../components/CardShell';
import { Text, Button } from '../components/Index';
import { ChakraFormRenderer } from '../helpers/parameter-renderer/backends/chakra';
import { useProfileForm } from '../features/profile';
import useAsyncData from '@/utils/AsyncData';
import { getActiveWorker } from '@/service/db/Worker';

const UpdateProfile: React.FC = () => {
  const navigate = useNavigate();

  const activeWorkerData = useAsyncData(getActiveWorker);
  const { formCtx, handleSubmit, isSubmitting, submitError, setForm } = useProfileForm({
    onSuccess: () => {
      // Redirect to dashboard on success
      void navigate('/dashboard', { replace: true });
    },
  });

  const existingProfile = activeWorkerData.data?.getOk()?.profile?.data || null;
  const isEditing = Boolean(existingProfile);
  useEffect(() => {
    // If profile already exists, prefill the form for editing
    if (existingProfile) {
      const normalizedProfile = {
        ...existingProfile,
        gender: existingProfile.gender?.[0]?.toLowerCase().replaceAll(' ', '_') || '',
      };
      const educationEntries: Array<Record<'degree' | 'year' | 'college', string>> = existingProfile.education || [];
      educationEntries.forEach((education, index) => {
        const prefix = index === 0 ? 'latest' : `previous`;
        Object.keys(education).forEach((key) => {
          switch (key) {
            case 'degree':
              normalizedProfile[`${prefix}_degree`] = education.degree;
              break;
            case 'year':
              normalizedProfile[`${prefix}_degree_year`] = education.year;
              break;
            case 'college':
              normalizedProfile[`${prefix}_degree_college`] = education.college;
              break;
            default:
              break;
          }
        });
      });
      setForm(normalizedProfile);
    }
  }, [existingProfile, setForm]);

  if (activeWorkerData.isLoading) {
    // TODO: Add loading state UI
    return null;
  }

  return (
    <Box minH="100vh" bg="bg.primary">
      <MinimalUserHeader />
      <Flex minH="calc(100vh - 112px)" align="center" justify="center" p="4">
        <CardShell
          w={{ base: '100%', md: '900px' }}
          contentProps={{
            bg: 'bg.primary',
            border: '1px solid',
            borderColor: 'brand.teal',
            borderRadius: '4px',
          }}
        >
          <VStack gap="40px" py="60px" px={{ base: '24px', md: '90px' }} w="100%">
            <Text as="h1" variant="heroHeading">
              Profile Registration
            </Text>

            {submitError && (
              <Box w="100%" p={4} bg="error.bg" borderRadius="md" border="1px solid" borderColor="error.border">
                <Text variant="body" color="error.text">
                  {submitError}
                </Text>
              </Box>
            )}

            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <ChakraFormRenderer ctx={formCtx} />
              <Center mt="40px">
                <Button
                  type="submit"
                  variant="cta"
                  w={{ base: '100%', md: '200px' }}
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                >
                  {isEditing
                    ? // If editing existing profile
                      isSubmitting
                      ? 'Updating...'
                      : 'Update Profile'
                    : // If creating new profile
                      isSubmitting
                      ? 'Submitting...'
                      : 'Submit Profile'}
                </Button>
              </Center>
            </form>
          </VStack>
        </CardShell>
      </Flex>
    </Box>
  );
};

export default UpdateProfile;
